"use client";

import { cn } from "@/lib/utils";
import { WIDTH_1080P } from "@constants/global";
import { useWindowWidth } from "@react-hook/window-size";
import { Check, ChevronsUpDown, X } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import * as React from "react";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "../../command";
import { Popover, PopoverContent, PopoverTrigger } from "../../popOver";

interface SearchBoxMultiselectProps {
  readonly selectedFilterTagValues: string[] | undefined;
  readonly setSelectedFilterTagValues?: React.Dispatch<
    React.SetStateAction<string[]>
  >;
  readonly tags: string[] | undefined;
  readonly placeholder?: string;
  readonly placeholderSearch?: string;
  readonly messageNoFound?: string;
  readonly showBadge?: boolean; // Nueva prop para mostrar Badge
  readonly className?: string;
}

export default function SearchBoxMultiselect(props: SearchBoxMultiselectProps) {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation("explore");
  const wWidth = useWindowWidth();
  const is1080p = wWidth >= WIDTH_1080P;

  const {
    selectedFilterTagValues,
    setSelectedFilterTagValues,
    tags = [],
    placeholder,
    placeholderSearch,
    messageNoFound,
    showBadge = true // Valor por defecto
  } = props;

  const toggleSelection = (currentValue: string) => {
    setSelectedFilterTagValues?.((prevSelected: string[]) =>
      prevSelected.includes(currentValue)
        ? prevSelected.filter((value) => value !== currentValue)
        : [...prevSelected, currentValue]
    );
  };

  const removeSelection = (valueToRemove: string) => {
    setSelectedFilterTagValues?.((prevSelected: string[]) =>
      prevSelected.filter((value) => value !== valueToRemove)
    );
  };

  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`min-h-8 w-full min-w-[80px] flex-wrap justify-between border px-2 py-1 dark:border-gray-800 fhd:min-w-[160px] ${props.className}`}
          >
            {(selectedFilterTagValues ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {showBadge ? ( // Verifica si mostrar Badge
                  (selectedFilterTagValues ?? []).map((value) => (
                    <Badge
                      key={value}
                      variant="default"
                      className="pointer-events-auto text-xs"
                    >
                      {value}
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Evita que se abra el Popover al hacer clic en el Badge
                          removeSelection(value);
                        }}
                        className="ml-1 rounded-full border border-white hover:border-red-500 hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))
                ) : (
                  // Mostrar contador
                  <span className="text-xs">
                    {t("Filtros", {
                      numberOfTagsSelected: `(${selectedFilterTagValues?.length ?? 0})`
                    })}
                  </span>
                )}
              </div>
            ) : (
              (placeholder ?? (
                <span className="text-xs">
                  {is1080p ? t("Filtrar por tags") : t("Filtrar")}
                </span>
              ))
            )}

            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[200px] border p-0 dark:border-gray-600"
        >
          <Command className="z-20">
            <CommandInput
              placeholder={`${placeholderSearch ?? t("Buscar por tags")}`}
              className="text-xs"
            />
            <CommandList>
              <CommandEmpty>{`${messageNoFound ?? t("Tag no encontrado")}`}</CommandEmpty>
              <CommandGroup>
                {tags.map((tag) => (
                  <CommandItem
                    key={tag}
                    value={tag}
                    onSelect={() => toggleSelection(tag)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        (selectedFilterTagValues ?? []).includes(tag)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {tag}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
